/**
 * Номенклатура — справочник SKU (~3000 позиций).
 * Кнопка «+ Новая позиция» открывает модалку с формой создания.
 */
import { Fragment, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import { EditableCell } from "@/components/warehouse/EditableCell";
import { createItem, fetchItems, fetchSections, fetchUnits, patchItem, formatRub, type ItemCreate, type ItemPatch, type ItemRow } from "@/api/warehouse";
import { usePermissions } from "@/lib/usePermissions";
import { ApiError } from "@/api/client";


export function WarehouseItemsPage() {
  const can = usePermissions();
  const [warehouse, setWarehouse] = useState<"" | "СКЛАД 2_4" | "ИНСТРУМЕНТ 1.0" | "ОФИС">("");
  const [section, setSection] = useState("");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const qc = useQueryClient();

  const itemsQ = useQuery({
    queryKey: ["warehouse-items", warehouse, section, search],
    queryFn: () => fetchItems({
      warehouse: warehouse || undefined,
      section: section || undefined,
      search: search || undefined,
      limit: 2000,
    }),
  });
  const sectionsQ = useQuery({
    queryKey: ["warehouse-sections"],
    queryFn: () => fetchSections(),
  });
  const unitsQ = useQuery({
    queryKey: ["warehouse-units"],
    queryFn: () => fetchUnits(),
    staleTime: 5 * 60 * 1000,
  });

  async function saveItemField(item: ItemRow, patch: ItemPatch) {
    setSavingId(item.id);
    try {
      await patchItem(item.id, patch);
      await qc.invalidateQueries({ queryKey: ["warehouse-items"] });
      await qc.invalidateQueries({ queryKey: ["warehouse-sections"] });
      await qc.invalidateQueries({ queryKey: ["warehouse-units"] });
      await qc.invalidateQueries({ queryKey: ["warehouse-items-audit"] });
    } finally {
      setSavingId(null);
    }
  }

  if (itemsQ.isLoading) return <Spinner size="lg" label="Загрузка номенклатуры…" />;
  if (itemsQ.error) {
    const err = itemsQ.error;
    if (err instanceof ApiError && err.status === 403) {
      return <div className="alert alert-amber"><div className="alert-body">Доступ ограничен</div></div>;
    }
    return <div className="alert alert-red"><div className="alert-body">{(err as Error).message}</div></div>;
  }

  const data = itemsQ.data!;
  const sections = sectionsQ.data || [];

  // Группировка по секции для UX
  const grouped: Record<string, typeof data.items> = {};
  for (const it of data.items) {
    const sec = it.section || "—";
    if (!grouped[sec]) grouped[sec] = [];
    grouped[sec].push(it);
  }

  return (
    <div className="page">

      <div className="filter-bar" style={{ justifyContent: "space-between", marginBottom: "var(--gap)", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select className="input-sm" value={warehouse} onChange={e => setWarehouse(e.target.value as "СКЛАД 2_4" | "ИНСТРУМЕНТ 1.0" | "ОФИС" | "")}>
            <option value="">Все склады</option>
            <option value="СКЛАД 2_4">СКЛАД 2_4</option>
            <option value="ИНСТРУМЕНТ 1.0">ИНСТРУМЕНТ 1.0</option>
            <option value="ОФИС">ОФИС</option>
          </select>
          <select className="input-sm" value={section} onChange={e => setSection(e.target.value)} style={{ minWidth: 200 }}>
            <option value="">Все разделы</option>
            {sections.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="search" className="input-sm" placeholder="Поиск по артикулу / наименованию"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ minWidth: 320 }}
          />
          {can("items.edit") && (
            <button
              type="button" className="btn btn-primary"
              onClick={() => setCreateOpen(true)}
              style={{ background: "var(--brick)", color: "#fff", border: "none", padding: "6px 14px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
            >
              + Новая позиция
            </button>
          )}
        </div>
      </div>

      <div className="row row-3" style={{ marginBottom: "var(--gap)" }}>
        <div className="kpi">
          <div className="kpi-label">Найдено</div>
          <div className="kpi-value kpi-value-sm">{data.items.length.toLocaleString("ru-RU")}</div>
          <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>из {data.total.toLocaleString("ru-RU")}</span></div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Разделов</div>
          <div className="kpi-value kpi-value-sm">{sections.length}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Складов</div>
          <div className="kpi-value kpi-value-sm">2</div>
        </div>
      </div>

      {data.items.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "32px 20px" }}>
          <div className="muted">Позиций не найдено</div>
        </div>
      ) : (
        <div className="card">
          <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 120 }}>Артикул</th>
                  <th>Наименование</th>
                  <th style={{ width: 70 }}>Ед.</th>
                  <th style={{ width: 130 }}>Склад</th>
                  <th className="num" style={{ width: 110 }}>Цена закуп</th>
                  <th className="num" style={{ width: 110 }}>Цена розн.</th>
                  <th style={{ width: 50 }} title="Карточка"></th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(grouped).map(([sec, group]) => (
                  <Fragment key={sec}>
                    <tr style={{ background: "var(--paper)" }}>
                      <td colSpan={7} style={{ fontWeight: 700, fontSize: 13, color: "var(--ink-3)" }}>
                        {sec} <span style={{ color: "var(--mute)", fontWeight: 400 }}>· {group.length}</span>
                      </td>
                    </tr>
                    {group.map(it => {
                      const isSaving = savingId === it.id;
                      return (
                        <tr key={it.id}>
                          <td className="tabular" style={{ padding: 0 }}>
                            <EditableCell
                              kind="text"
                              value={it.sku}
                              saving={isSaving}
                              onSave={(v) => saveItemField(it, { sku: v })}
                            />
                          </td>
                          <td style={{ padding: 0 }}>
                            <EditableCell
                              kind="text"
                              value={it.name}
                              saving={isSaving}
                              onSave={(v) => v ? saveItemField(it, { name: v }) : undefined}
                            />
                          </td>
                          <td style={{ padding: 0 }}>
                            <EditableCell
                              kind="select"
                              value={it.unit}
                              options={unitsQ.data || []}
                              saving={isSaving}
                              onSave={(v) => saveItemField(it, { unit: v })}
                            />
                          </td>
                          <td style={{ fontSize: 12, color: "var(--mute)" }}>{it.warehouse || "—"}</td>
                          <td className="num tabular" style={{ padding: 0 }}>
                            <EditableCell
                              kind="number"
                              align="right"
                              value={it.price_buy}
                              saving={isSaving}
                              format={(v) => v === null || v === "" ? "—" : formatRub(parseFloat(String(v)))}
                              onSave={(v) => saveItemField(it, { price_buy: v === null ? null : parseFloat(v.replace(",", ".")) })}
                            />
                          </td>
                          <td className="num tabular" style={{ padding: 0 }}>
                            <EditableCell
                              kind="number"
                              align="right"
                              value={it.price_sell}
                              saving={isSaving}
                              format={(v) => v === null || v === "" ? "—" : formatRub(parseFloat(String(v)))}
                              onSave={(v) => saveItemField(it, { price_sell: v === null ? null : parseFloat(v.replace(",", ".")) })}
                            />
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <Link to={`/items/${it.id}/card` as "/"} title="Карточка позиции" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}><Icon name="trend" size={14}/></Link>
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {createOpen && (
        <CreateItemModal
          sections={sections}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </div>
  );
}


function CreateItemModal({ sections, onClose }: { sections: string[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<ItemCreate>({
    name: "", sku: "", unit: "", section: "", warehouse: "",
    price_buy: null, price_sell: null, note: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [creatingSection, setCreatingSection] = useState(false);

  const unitsQ = useQuery({
    queryKey: ["warehouse-units"],
    queryFn: () => fetchUnits(),
    staleTime: 5 * 60 * 1000,
  });
  const units = unitsQ.data || [];

  const mut = useMutation({
    mutationFn: () => createItem({
      name: form.name.trim(),
      sku: form.sku?.trim() || null,
      unit: form.unit?.trim() || null,
      section: form.section?.trim() || null,
      warehouse: form.warehouse?.trim() || null,
      price_buy: form.price_buy ?? null,
      price_sell: form.price_sell ?? null,
      note: form.note?.trim() || null,
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["warehouse-items"] });
      void qc.invalidateQueries({ queryKey: ["warehouse-sections"] });
      void qc.invalidateQueries({ queryKey: ["warehouse-units"] });
      onClose();
    },
    onError: (e) => {
      const msg = e instanceof ApiError
        ? ((e.body as { detail?: string })?.detail || `HTTP ${e.status}`)
        : (e as Error).message;
      setError(msg);
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError("Введите наименование позиции");
      return;
    }
    mut.mutate();
  }

  return (
    <div
      role="dialog"
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(0,0,0,0.4)",
        display: "grid", placeItems: "center", padding: 16,
      }}
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ width: "100%", maxWidth: 520, padding: 20, background: "var(--card)" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontFamily: "var(--serif)", fontSize: 20, fontWeight: 700 }}>Новая позиция</div>
          <button type="button" onClick={onClose} style={{ background: "transparent", border: "none", fontSize: 20, cursor: "pointer", color: "var(--mute)" }}>×</button>
        </div>

        <Field label="Наименование *">
          <input
            type="text" className="input-sm" style={{ width: "100%" }} required autoFocus
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder='Например: "Доска 25х100х6000"'
          />
        </Field>

        <Field label="Артикул">
          <input
            type="text" className="input-sm" style={{ width: "100%" }}
            value={form.sku || ""} onChange={(e) => setForm({ ...form, sku: e.target.value })}
            placeholder="опционально"
          />
        </Field>

        <Field label="Раздел">
          {creatingSection ? (
            <div style={{ display: "flex", gap: 4 }}>
              <input
                type="text" className="input-sm" style={{ flex: 1 }}
                value={form.section || ""} onChange={(e) => setForm({ ...form, section: e.target.value })}
                placeholder="Новый раздел"
                autoFocus
              />
              <button type="button" className="btn btn-sm" onClick={() => { setCreatingSection(false); setForm({ ...form, section: "" }); }}>×</button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 4 }}>
              <select
                className="input-sm" style={{ flex: 1 }}
                value={form.section || ""} onChange={(e) => setForm({ ...form, section: e.target.value })}
              >
                <option value="">— Без раздела —</option>
                {sections.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <button type="button" className="btn btn-sm" onClick={() => { setCreatingSection(true); setForm({ ...form, section: "" }); }} title="Создать новый раздел">+</button>
            </div>
          )}
        </Field>

        <Field label="Ед. измерения">
          <input
            type="text" className="input-sm" style={{ width: "100%" }} list="create-item-units"
            value={form.unit || ""} onChange={(e) => setForm({ ...form, unit: e.target.value })}
            placeholder="шт / м / кг / уп …"
          />
          <datalist id="create-item-units">
            {units.map(u => <option key={u} value={u} />)}
          </datalist>
        </Field>

        <Field label="Склад">
          <select
            className="input-sm" style={{ width: "100%" }}
            value={form.warehouse || ""} onChange={(e) => setForm({ ...form, warehouse: e.target.value })}
          >
            <option value="">— Не указан —</option>
            <option value="СКЛАД 2_4">СКЛАД 2_4</option>
            <option value="ИНСТРУМЕНТ 1.0">ИНСТРУМЕНТ 1.0</option>
            <option value="ОФИС">ОФИС</option>
          </select>
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <Field label="Цена закуп, ₽">
            <input
              type="number" inputMode="decimal" min="0" step="0.01" className="input-sm" style={{ width: "100%" }}
              value={form.price_buy ?? ""}
              onChange={(e) => setForm({ ...form, price_buy: e.target.value === "" ? null : parseFloat(e.target.value) })}
            />
          </Field>
          <Field label="Цена розн., ₽">
            <input
              type="number" inputMode="decimal" min="0" step="0.01" className="input-sm" style={{ width: "100%" }}
              value={form.price_sell ?? ""}
              onChange={(e) => setForm({ ...form, price_sell: e.target.value === "" ? null : parseFloat(e.target.value) })}
            />
          </Field>
        </div>

        <Field label="Примечание">
          <textarea
            className="input-sm" style={{ width: "100%", minHeight: 60, resize: "vertical" }}
            value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })}
          />
        </Field>

        {error && <div className="alert alert-red" style={{ marginTop: 8 }}><div className="alert-body">{error}</div></div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button type="button" className="btn" onClick={onClose} disabled={mut.isPending}>Отмена</button>
          <button
            type="submit" className="btn btn-primary" disabled={mut.isPending || !form.name.trim()}
            style={{ background: "var(--brick)", color: "#fff", border: "none", padding: "6px 18px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
          >
            {mut.isPending ? "Создание…" : "Создать"}
          </button>
        </div>
      </form>
    </div>
  );
}


function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}
